import {
  ArchiveIcon,
  CaretUpDownIcon,
  ClockClockwiseIcon,
  DotsThreeVerticalIcon,
} from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns/format';
import { useState } from 'react';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { ItemStatusEnum } from '@/modules/common';
import {
  ChangeStatusDialog,
  GenericTable,
  TableItemStatus,
} from '@/modules/common/components';

import { Mask } from '../types';

interface MasksTableProps {
  data: Mask[];
  onClick: (item: Mask) => void;
  toggleStatus: (item: Mask) => void;
}

export function MasksTable({ data, toggleStatus, onClick }: MasksTableProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeMask, setActiveMask] = useState<Mask | null>(null);
  const isArchived = activeMask?.status === ItemStatusEnum.Archived;

  const onArchiveRestoreClick = (
    event: React.SyntheticEvent<HTMLDivElement>,
    maskRow: Mask
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveMask(maskRow);
  };

  const toggleModal = (nextOpenState: boolean) => {
    if (!nextOpenState) {
      setActiveMask(null);
    }
  };

  const toggleStatusMask = async () => {
    if (activeMask?.id) {
      setIsUpdating(true);
      try {
        await toggleStatus(activeMask as Mask);
        setActiveMask(null);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const columns: ColumnDef<Mask>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Mask Name
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('name')}</span>
      ),
    },
    {
      accessorKey: 'imagesPublicUrls',
      header: 'Preview',
      cell: ({ row }) => {
        const images = Object.values({
          large: row.original.imagesPublicUrls?.large,
          medium: row.original.imagesPublicUrls?.transparent,
        });

        return (
          <div className="flex gap-2">
            {images.map((image, index) => (
              <img
                key={index}
                src={image}
                alt="Mask preview image"
                className="w-10 h-10 rounded-lg"
              />
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Status
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => <TableItemStatus status={row.getValue('status')} />,
    },
    {
      accessorKey: 'updatedAt',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Upload Date
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) =>
        format(new Date(row.getValue('updatedAt')), 'yyyy-MM-dd'),
    },
    {
      size: 40,
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const isArchived = row.original.status === ItemStatusEnum.Archived;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 p-0"
                data-testid="masks-table-actions"
              >
                <span className="sr-only">Open menu</span>
                <DotsThreeVerticalIcon color={colors.zinc[400]} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                data-testid={isArchived ? 'restore' : 'archive'}
                className={cn(
                  !isArchived && 'focus:text-destructive text-destructive'
                )}
                onClick={(event) => onArchiveRestoreClick(event, row.original)}
              >
                {isArchived ? (
                  <ClockClockwiseIcon />
                ) : (
                  <ArchiveIcon color="hsl(var(--destructive))" />
                )}
                {isArchived ? 'Restore' : 'Archive'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <>
      <GenericTable
        data={data}
        onClick={onClick}
        columns={columns}
        searchable
        searchColumns={['name']}
        searchPlaceholder="Search by mask name"
        emptyDescription="No masks uploaded yet"
      />

      <ChangeStatusDialog
        onOpenChange={toggleModal}
        isArchived={isArchived}
        isUpdating={isUpdating}
        onSubmit={toggleStatusMask}
        isOpen={Boolean(activeMask?.id)}
        title={`${isArchived ? 'Restore' : 'Archive'} mask “${activeMask?.name}”?`}
        restoreDescription="Are you sure you want to restore this mask?"
        archiveDescription="Are you sure you want to archive this mask? You can restore it later if needed."
      />
    </>
  );
}
